// Owns two things about the VenuePilot event id: what counts as one, and how it reaches
// the GTM dataLayer so GA4 rows can be attributed to the event (and, joined against
// events_view_latest in BigQuery, to the client) that produced them.
//
// `asEventId` is the first of those and is exported, because the same rule decides
// whether an id is fit to send as a catalog key to Meta, Reddit and TikTok — see
// trackingPayloads.ts. One definition, so a slug can never be a product id on one queue
// and rejected on another. The rest of this file is the second.
//
// `window.dataLayer` has two readers, and the whole design turns on how each decides
// what to send:
//
//   - gtag.js, loaded by TrackingProvider and pointed at each client's OWN GA property
//     via gtag('config', trackingId), takes a hit's parameters from the commands it is
//     given — the `arguments` objects gtag() pushes. A plain-object entry is never one
//     of those, so it cannot become a parameter on a client's hit.
//   - gtm.js, the VenuePilot container, merges plain-object pushes into its data model,
//     where a Data Layer Variable can read them on every tag that fires afterwards.
//
// So a plain-object push reaches our container on every hit while staying off the
// client-owned GA hits; fbq, ttq and rdt have their own queues and never see it. The
// rule that follows: this id must never appear in a gtag COMMAND — not in a trackEvent
// payload, not in gtag('set', ...), not in the config params of gtag('config', id, {}).
// TrackingProvider.spec.ts pins every command on the dataLayer, not just trackEvent's.
//
// See packages/checkout/doc/claude/analytics.md for the container and GA4 half.

import { watch, type WatchStopHandle } from 'vue';

/** The single dataLayer key. Prefixed so it cannot collide with a client's own keys. */
export const VP_EVENT_ID_KEY = 'vp_event_id';

type DataLayerWindow = Window & { dataLayer?: unknown[] };

/**
 * The slice of vike's pageContext the seed reads. Declared locally because
 * `_piniaInitialState` is untyped across this package.
 */
export type EventIdPageContext = {
  routeParams?: Record<string, string>;
  _piniaInitialState?: {
    checkout?: {
      selectedEvent?: { id?: number };
      orderSummary?: { event?: { id?: number } };
    };
  };
};

/**
 * A VenuePilot event id, or `undefined` for anything that is not one.
 *
 * Strict about strings because the route param it guards is user-controlled and
 * `/e/:event_id` also accepts a slug: bare `Number()` would turn `'0x2A'` into 42 and
 * `'1e3'` into 1000. `isSafeInteger` rejects ids past 2^53, which would otherwise
 * arrive as a different — and still integral — number.
 */
export const asEventId = (eventId?: number | string | null): number | undefined => {
  if (typeof eventId === 'string' && !/^\d+$/.test(eventId)) {
    return undefined;
  }

  const id = Number(eventId);

  if (!Number.isSafeInteger(id) || id <= 0) {
    return undefined;
  }

  return id;
};

/** What the dataLayer currently says the event is; the newest entry wins. */
const publishedEventId = (dataLayer: unknown[]): number | undefined => {
  for (let i = dataLayer.length - 1; i >= 0; i--) {
    const entry = dataLayer[i];

    if (
      typeof entry === 'object' &&
      entry !== null &&
      Object.prototype.hasOwnProperty.call(entry, VP_EVENT_ID_KEY)
    ) {
      return (entry as Record<string, number>)[VP_EVENT_ID_KEY];
    }
  }

  return undefined;
};

/**
 * Publishes `eventId` to the dataLayer, once.
 *
 * An absent id publishes **nothing** rather than clearing what is already there. That
 * distinction matters because "there is no event" and "the event has not loaded yet"
 * are the same value here: on every route that does not server-render, the store is
 * still empty when TrackingProvider mounts, so a clearing push would blank the id the
 * seed just published and every tag on a DOM Ready or History Change trigger would read
 * nothing.
 *
 * The cost is that a published id outlives its event within one document. That is
 * harmless — checkout serves one event per document, and reaching another one is a new
 * navigation — whereas the blanking is not.
 */
export const pushEventId = (eventId?: number | string | null): void => {
  if (import.meta.env.SSR) {
    return;
  }

  const id = asEventId(eventId);

  if (id === undefined) {
    return;
  }

  const target = window as unknown as DataLayerWindow;
  target.dataLayer = target.dataLayer || [];

  // Read back rather than memoise in module scope: the dataLayer is per document, so
  // it is the honest source of what is currently published, and it needs no reset hook
  // to keep tests from bleeding into each other.
  if (publishedEventId(target.dataLayer) === id) {
    return;
  }

  target.dataLayer.push({ [VP_EVENT_ID_KEY]: id });
};

/**
 * Seeds the dimension from the page context, before the GTM container script exists.
 *
 * The first two branches mirror the store's `getSelectedEvent`
 * (`orderSummary?.event || selectedEvent`) at the object level, so the seed and the
 * watcher below pick the same event. The order-summary branch is unreachable today —
 * the only page that serialises an `orderSummary` is the print-ticket view, whose
 * fragment omits `event` — and is kept as that consistency guarantee, not as coverage.
 *
 * The route-param branch has no getter analogue. It exists because `stripeReturnUrl()`
 * brings the buyer back to `/e/<numeric id>/finalize` as a full document load that does
 * not server-render, and it is used only when the param is numeric, since the same
 * route also accepts a slug.
 */
export const seedEventIdFromPageContext = (pageContext: EventIdPageContext): void => {
  const checkout = pageContext?._piniaInitialState?.checkout;
  const event = checkout?.orderSummary?.event ?? checkout?.selectedEvent;

  pushEventId(event?.id ?? pageContext?.routeParams?.event_id);
};

/**
 * Keeps the dimension current for everything the seed cannot see: client-side
 * navigation, and an event that only resolves after the page has loaded.
 *
 * `flush: 'sync'` is load-bearing. With the default `'pre'` the push is deferred to the
 * pre-render queue, so a `gtag('event', ...)` fired in the same tick as the store update
 * would land in the dataLayer ahead of the id and silently lose the dimension on that
 * hit.
 */
export const trackEventIdDimension = (
  source: () => number | string | undefined,
): WatchStopHandle => watch(
  source,
  id => pushEventId(id),
  { immediate: true, flush: 'sync' },
);
